import { db } from '../config/firebase';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { COUNTRIES } from '../constants/countries';

export interface CodeListItem {
  id: string;
  code: string;
  verboseName: string;
  type: string;
  object: string;
  sortOrder?: number;
}

export interface NationalityCode extends CodeListItem {
  type: 'alpha3' | 'phoneCode';
  object: 'nationality';
}

export interface CountryOption {
  code: string;
  name: string;
  isCommon: boolean;
  sortOrder: number;
}

// Cache variables to reduce Firestore reads
let nationalityCache: NationalityCode[] | null = null;
let phoneCodeCache: NationalityCode[] | null = null;
let countryOptionsCache: CountryOption[] | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const COLL = 'codeLists';

/**
 * Fetch all items of a given type+object, ordered by sortOrder
 */
export const listCodeList = async (
  type: string,
  object: string
): Promise<CodeListItem[]> => {
  const q = query(
    collection(db, COLL),
    where('type', '==', type),
    where('object', '==', object),
    orderBy('sortOrder', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
};

/**
 * Add a new code list item
 */
export const addCodeListItem = async (
  payload: Omit<CodeListItem, 'id'>
): Promise<void> => {
  await addDoc(collection(db, COLL), payload);
};

/**
 * Delete a code list item by ID
 */
export const deleteCodeListItem = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, COLL, id));
};

/**
 * Get a single code list item by object, type, and code
 * Returns the item with sortOrder and verboseName, or null if not found
 */
export const getCodeListByCode = async (
  object: string,
  type: string,
  code: string
): Promise<CodeListItem | null> => {
  const q = query(
    collection(db, COLL),
    where('object', '==', object),
    where('type', '==', type),
    where('code', '==', code)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const doc = snap.docs[0];
  return { id: doc.id, ...(doc.data() as any) };
};

/**
 * Get a single code list item by object, type, and sortOrder
 * Returns the item with code and verboseName, or null if not found
 */
export const getCodeListBySortOrder = async (
  object: string,
  type: string,
  sortOrder: number
): Promise<CodeListItem | null> => {
  const q = query(
    collection(db, COLL),
    where('object', '==', object),
    where('type', '==', type),
    where('sortOrder', '==', sortOrder)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const doc = snap.docs[0];
  return { id: doc.id, ...(doc.data() as any) };
};

/**
 * Get verbose name for a code list item by object, type, and code
 * Returns the verboseName string, or a fallback if not found
 */
export const getVerboseName = async (
  object: string,
  type: string,
  code: string,
  fallback: string = code
): Promise<string> => {
  const item = await getCodeListByCode(object, type, code);
  return item?.verboseName || fallback;
};

/**
 * Check if cache is still valid
 */
const isCacheValid = (): boolean => {
  return Date.now() - cacheTimestamp < CACHE_DURATION;
};

/**
 * Clear cache (useful for testing or when data changes)
 */
export const clearCodeListCache = (): void => {
  nationalityCache = null;
  phoneCodeCache = null;
  countryOptionsCache = null;
  cacheTimestamp = 0;
};

/**
 * Fetch nationality alpha3 codes with caching and error handling
 * Only returns countries with sortOrder < 500 (common + active countries)
 */
export const fetchNationalityCodes = async (): Promise<NationalityCode[]> => {
  try {
    if (nationalityCache && isCacheValid()) {
      return nationalityCache;
    }

    const q = query(
      collection(db, COLL),
      where('object', '==', 'nationality'),
      where('type', '==', 'alpha3'),
      where('sortOrder', '<', 500),
      orderBy('sortOrder', 'asc')
    );

    const snap = await getDocs(q);
    nationalityCache = snap.docs.map(d => ({ 
      ...(d.data() as NationalityCode),
      id: d.id
    }));
    
    cacheTimestamp = Date.now();
    return nationalityCache;
  } catch (error) {
    console.warn('Failed to fetch nationality codes from Firestore, using fallback:', error);
    // Fallback to hardcoded data
    return COUNTRIES.map(country => ({
      id: `nationality_alpha3_${country.code}`,
      code: country.code,
      verboseName: country.name,
      type: 'alpha3' as const,
      object: 'nationality' as const,
      sortOrder: country.isCommon ? 1 : 50
    }));
  }
};

/**
 * Fetch phone codes with caching and error handling
 * Only returns countries with sortOrder < 500 (common + active countries)
 */
export const fetchPhoneCodes = async (): Promise<NationalityCode[]> => {
  try {
    if (phoneCodeCache && isCacheValid()) {
      return phoneCodeCache;
    }

    const q = query(
      collection(db, COLL),
      where('object', '==', 'nationality'),
      where('type', '==', 'phoneCode'),
      where('sortOrder', '<', 500),
      orderBy('sortOrder', 'asc')
    );

    const snap = await getDocs(q);
    phoneCodeCache = snap.docs.map(d => ({ 
      ...(d.data() as NationalityCode),
      id: d.id
    }));
    
    cacheTimestamp = Date.now();
    return phoneCodeCache;
  } catch (error) {
    console.warn('Failed to fetch phone codes from Firestore, using fallback:', error);
    // Fallback to hardcoded data (using common phone codes)
    const fallbackPhoneCodes = [
      { code: '47', verboseName: 'Norway', sortOrder: 1 },
      { code: '46', verboseName: 'Sweden', sortOrder: 2 },
      { code: '45', verboseName: 'Denmark', sortOrder: 3 },
      { code: '358', verboseName: 'Finland', sortOrder: 4 },
      { code: '1', verboseName: 'United States', sortOrder: 50 },
      { code: '44', verboseName: 'United Kingdom', sortOrder: 51 },
      { code: '49', verboseName: 'Germany', sortOrder: 52 },
      { code: '33', verboseName: 'France', sortOrder: 53 },
    ];
    
    return fallbackPhoneCodes.map(item => ({
      id: `nationality_phoneCode_${item.code}`,
      code: item.code,
      verboseName: item.verboseName,
      type: 'phoneCode' as const,
      object: 'nationality' as const,
      sortOrder: item.sortOrder
    }));
  }
};

/**
 * Fetch country options for UI components with caching
 * Returns array of CountryOption objects with isCommon flag
 */
export const fetchCountryOptions = async (): Promise<CountryOption[]> => {
  try {
    if (countryOptionsCache && isCacheValid()) {
      return countryOptionsCache;
    }

    const nationalityCodes = await fetchNationalityCodes();
    
    countryOptionsCache = nationalityCodes.map(code => ({
      code: code.code,
      name: code.verboseName,
      isCommon: (code.sortOrder ?? 999) <= 4, // Common countries have sortOrder 1-4
      sortOrder: code.sortOrder ?? 999
    }));
    
    return countryOptionsCache;
  } catch (error) {
    console.warn('Failed to fetch country options, using fallback:', error);
    // Fallback to hardcoded data
    return COUNTRIES.map(country => ({
      code: country.code,
      name: country.name,
      isCommon: country.isCommon || false,
      sortOrder: country.isCommon ? 1 : 50
    }));
  }
};

/**
 * Get country name by code with translation support
 * This is a convenience function that combines Firestore data with i18n
 */
export const getCountryName = async (
  code: string,
  fallback?: string
): Promise<string> => {
  try {
    const item = await getCodeListByCode('nationality', 'alpha3', code);
    return item?.verboseName || fallback || code;
  } catch (error) {
    console.warn(`Failed to get country name for ${code}:`, error);
    return fallback || code;
  }
};

/**
 * Get phone country code by nationality code
 */
export const getPhoneCodeByNationality = async (
  nationalityCode: string
): Promise<string | null> => {
  try {
    // First get the nationality to find its sortOrder
    const nationality = await getCodeListByCode('nationality', 'alpha3', nationalityCode);
    if (!nationality) return null;

    // Find phone code with same sortOrder
    const phoneCode = await getCodeListBySortOrder('nationality', 'phoneCode', nationality.sortOrder || 999);
    return phoneCode?.code || null;
  } catch (error) {
    console.warn(`Failed to get phone code for nationality ${nationalityCode}:`, error);
    return null;
  }
};
